
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Suspense, useEffect, type ReactNode } from "react";
import { lazyWithRetry } from "./utils/lazyWithRetry";
import { RouteErrorBoundary } from "./components/RouteErrorBoundary";
import { ThemeProvider as ColorSchemeProvider } from "next-themes";
import Index from "./pages/Index";
import Login from "./pages/Login";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { AnnouncementPopup } from "./components/AnnouncementPopup";
import { AuthProvider } from "./hooks/useAuth";
import { SettingsProvider } from "./contexts/SettingsContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { LanguageProvider } from "./i18n/LanguageContext";
import { MotionProvider } from "./components/motion";


// Import AdminDashboard directly - no lazy loading
import AdminDashboard from "@/pages/AdminDashboard";
import { ProtectedAdminRoute } from "@/components/ProtectedAdminRoute";

// Lazy load all non-home pages to reduce initial bundle size
const NewsDetail = lazyWithRetry(() => import("@/pages/NewsDetail"));
const NewsPage = lazyWithRetry(() => import("@/pages/NewsPage").then(module => ({ default: module.NewsPage })));
const WebProgramming = lazyWithRetry(() => import("@/pages/WebProgramming"));
const Frontend = lazyWithRetry(() => import("@/pages/Frontend"));
const Backend = lazyWithRetry(() => import("@/pages/Backend"));
const ArtificialIntelligence = lazyWithRetry(() => import("@/pages/ProgrammingLanguages"));
const Cybersecurity = lazyWithRetry(() => import("@/pages/Cybersecurity"));
const AdminLogin = lazyWithRetry(() => import("@/pages/AdminLogin"));
const ResetPassword = lazyWithRetry(() => import("@/pages/ResetPassword"));
const LecturerDashboard = lazyWithRetry(() => import("@/pages/LecturerDashboard"));
const StudentDashboard = lazyWithRetry(() => import("@/pages/StudentDashboard"));
const RegistrationTest = lazyWithRetry(() => import("@/pages/RegistrationTest"));
const PrivacyPolicy = lazyWithRetry(() => import("@/pages/PrivacyPolicy"));
const RefundPolicy = lazyWithRetry(() => import("@/pages/RefundPolicy"));
const PaymentSuccess = lazyWithRetry(() => import("@/pages/PaymentSuccess"));
const PaymentFailed = lazyWithRetry(() => import("@/pages/PaymentFailed"));
const KidsProgramming = lazyWithRetry(() => import("@/pages/KidsProgramming"));
const Partnership = lazyWithRetry(() => import("@/pages/Partnership"));
const GiftCards = lazyWithRetry(() => import("@/pages/GiftCards"));
const AdmissionPage = lazyWithRetry(() => import("@/pages/AdmissionPage"));
const ServicesPage = lazyWithRetry(() => import("@/pages/ServicesPage"));
const SocialRefundApplication = lazyWithRetry(() => import("@/pages/SocialRefundApplication"));
const AITeacherTraining = lazyWithRetry(() => import("@/pages/AITeacherTraining"));
const QATesting = lazyWithRetry(() => import("@/pages/QATesting"));
const CertificateVerification = lazyWithRetry(() => import("@/pages/CertificateVerification"));
const ContactPage = lazyWithRetry(() => import("@/pages/ContactPage"));

// Import CoursePage directly to fix module resolution issue
import CoursePage from "@/pages/CoursePage";

// Lazy load ServicePage
const ServicePage = lazyWithRetry(() => import("@/pages/ServicePage"));

const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-primary"></div>
  </div>
);

const queryClient = new QueryClient();

const RouteIndexingGuard = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

    const privatePrefixes = ['/admin', '/student', '/lecturer', '/login', '/reset-password', '/secure-admin', '/registration-test', '/payment'];
    const shouldBlock = privatePrefixes.some(prefix => pathname.startsWith(prefix));
    let robots = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement('meta');
      robots.name = 'robots';
      document.head.appendChild(robots);
    }
    robots.content = shouldBlock
      ? 'noindex, nofollow'
      : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
  }, [pathname]);

  return null;
};

const RouteBoundary = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  return <RouteErrorBoundary resetKey={pathname}>{children}</RouteErrorBoundary>;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ColorSchemeProvider attribute="class" defaultTheme="dark" enableSystem={false} storageKey="gita-theme" disableTransitionOnChange>
      <LanguageProvider>
        <MotionProvider>
          <TooltipProvider>
            <BrowserRouter>
              <AuthProvider>
                <SettingsProvider>
                  <ThemeProvider>
                    <Toaster />
                    <Sonner />
                    <RouteIndexingGuard />
                    <Header />
                    <AnnouncementPopup />
                    <div className="flex min-h-screen flex-col pt-[72px]">
                      <main className="flex-1">
                        <RouteBoundary>
                        <Routes>
                          <Route path="/" element={<Index />} />
                          <Route path="/login" element={<Login />} />
                          <Route path="/admin/dashboard" element={<ProtectedAdminRoute><AdminDashboard /></ProtectedAdminRoute>} />
                          <Route path="/course/:courseId" element={<CoursePage />} />
                        </Routes>

                        <Suspense fallback={<LoadingSpinner />}>
                          <Routes>
                            <Route path="/services/:serviceSlug" element={<ServicePage />} />
                            <Route path="/news" element={<NewsPage />} />
                            <Route path="/web-programming" element={<WebProgramming />} />
                            <Route path="/frontend" element={<Frontend />} />
                            <Route path="/backend" element={<Backend />} />
                            <Route path="/ai" element={<ArtificialIntelligence />} />
                            <Route path="/cybersecurity" element={<Cybersecurity />} />
                            <Route path="/reset-password" element={<ResetPassword />} />
                            <Route path="/lecturer/dashboard" element={<LecturerDashboard />} />
                            <Route path="/student/dashboard" element={<StudentDashboard />} />
                            <Route path="/secure-admin-gita2025" element={<AdminLogin />} />
                            <Route path="/news/:newsId" element={<NewsDetail />} />
                            <Route path="/registration-test" element={<RegistrationTest />} />
                            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                            <Route path="/refund-policy" element={<RefundPolicy />} />
                            <Route path="/payment/success" element={<PaymentSuccess />} />
                            <Route path="/payment/failed" element={<PaymentFailed />} />
                            <Route path="/kids-programming" element={<KidsProgramming />} />
                            <Route path="/partnership" element={<Partnership />} />
                            <Route path="/gift-cards" element={<GiftCards />} />
                            <Route path="/admission" element={<AdmissionPage />} />
                            <Route path="/services" element={<ServicesPage />} />
                            <Route path="/social-refund-application" element={<SocialRefundApplication />} />
                            <Route path="/ai-teacher-training" element={<AITeacherTraining />} />
                            <Route path="/courses/qa-testing" element={<QATesting />} />
                            <Route path="/certificate-verification" element={<CertificateVerification />} />
                            <Route path="/contact" element={<ContactPage />} />
                          </Routes>
                        </Suspense>
                        </RouteBoundary>
                      </main>
                      <Footer />
                    </div>
                  </ThemeProvider>
                </SettingsProvider>
              </AuthProvider>
            </BrowserRouter>
          </TooltipProvider>
        </MotionProvider>
      </LanguageProvider>
    </ColorSchemeProvider>
  </QueryClientProvider>
);

export default App;
